import {
    businessCashRegExp, numberRegexp, phoneFormat, phoneRegExp,
} from '@constants/regexp';
import { CashReceiptIssueType } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

/**
 * 현금영수증 신청 상태와 변경.
 * 선물·선불카드로 결제하는 금액이 있을 때만 신청할 수 있고, 저장해 둔 정보가 있으면 신청 상태로 시작한다.
 * 정보 저장은 여부만 켜 두고, 실제 저장은 주문을 요청할 때 한다.
 */
function useCashReceipt() {
    const dispatch = useDispatch();
    const {
        giftAmount, prepaidCardAmount, cashReceipt, cashReceiptError, saveCashReceipt,
    } = useCalculatedRequest();
    const isAvailable = giftAmount > 0 || prepaidCardAmount > 0;
    const isApplied = !!cashReceipt;
    const issueType = cashReceipt?.issueType;
    const isBusiness = issueType === 'BUSINESS';
    const formattedNumber = (cashReceipt?.issueNumber || '')
        .replace(isBusiness ? businessCashRegExp : phoneRegExp, phoneFormat);

    // 선물·선불카드를 쓰기 시작하면 저장해 둔 정보로 신청해 두고, 쓰지 않게 되면 미신청으로 돌린다
    useEffect(() => {
        if (isAvailable) {
            dispatch(orderActions.applySavedCashReceipt());
        } else {
            dispatch(orderActions.disableCashReceipt());
            dispatch(orderActions.clearCashReceiptError());
        }
    }, [isAvailable, dispatch]);

    const changeApplied = (applied: boolean) => {
        if (applied === isApplied) {
            return;
        }
        dispatch(applied ? orderActions.enableCashReceipt() : orderActions.disableCashReceipt());
        dispatch(orderActions.clearCashReceiptError());
    };
    const changeIssueType = (type: CashReceiptIssueType) => {
        if (type === issueType) {
            return;
        }
        dispatch(orderActions.setCashReceiptIssueType(type));
        dispatch(orderActions.clearCashReceiptError());
    };
    const changeIssueNumber = (value: string) => {
        dispatch(orderActions.setCashReceiptNo(value.replace(numberRegexp, '')));
        dispatch(orderActions.clearCashReceiptError());
    };
    const changeSave = (save: boolean) => {
        dispatch(orderActions.setSaveCashReceipt(save));
    };

    return {
        isAvailable,
        isApplied,
        issueType,
        isBusiness,
        formattedNumber,
        error: cashReceiptError,
        isSave: saveCashReceipt,
        changeApplied,
        changeIssueType,
        changeIssueNumber,
        changeSave,
    };
}

export default useCashReceipt;
